export type storageType = 'local' | 'session' | 'cookie';

export const useStorage = (defaultType: storageType = 'cookie') => {
    const _getStorage = (type: storageType): Storage => {
        switch (type) {
            case 'local':
                return localStorage;
            case 'session':
                return sessionStorage;
            default:
                return null;
        }
    };

    const _deleteByType = (key: string, type: storageType): void => {
        if (type === 'cookie') {
            document.cookie = `${key}=; path=/; domain=${window.location.hostname}; Max-Age=-99999999; SameSite=Lax`;
            return;
        }

        const storage = _getStorage(type);
        if (storage) {
            storage.removeItem(key);
        }
    };

    const _setByType = (key: string, item: any, type: storageType) => {
        const storageItem = JSON.stringify({ value: item });

        if (type === 'cookie') {
            document.cookie = `${key}=${storageItem}; path=/; domain=${window.location.hostname}; SameSite=Lax`;
            return;
        }

        const storage = _getStorage(type);
        if (storage) {
            storage.setItem(key, storageItem);
        }
    };

    const _getByType = <T>(key: string, type: storageType): T => {
        let storageItem: string;

        if (type === 'cookie') {
            const name = key + '=';
            const decodedCookie = decodeURIComponent(document.cookie);
            const ca = decodedCookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') {
                    c = c.substring(1);
                }
                if (c.indexOf(name) === 0) {
                    storageItem = c.substring(name.length, c.length);
                    break;
                }
            }
        } else {
            const storage = _getStorage(type);
            storageItem = storage ? storage.getItem(key) : null;
        }

        if (!storageItem) {
            return null;
        }

        try {
            const parsedItem = JSON.parse(storageItem);
            return parsedItem.value as T;
        } catch {
            return null;
        }
    };

    const setItem = (key: string, item: any, type: storageType = defaultType): boolean => {
        try {
            _setByType(key, item, type);
            return true;
        } catch {
            return false;
        }
    };

    const getItem = <T>(key: string, type: storageType = defaultType): T => {
        try {
            return _getByType<T>(key, type);
        } catch {
            return null;
        }
    };

    const deleteItem = (key: string, type: storageType = defaultType): void => {
        try {
            _deleteByType(key, type);
        } catch {}
    };

    return {
        setItem,
        getItem,
        deleteItem
    };
};
